<template>
  <div class="h-full flex flex-col px-3 py-6 pt-0">
    <!-- title -->
    <div class="px-2 flex items-center justify-between h-10 mb-2.5">
      <span>预览区</span>
      <div class="flex gap-2.5 opacity-70">
        <a-tooltip title="录制动图">
          <icon
            class="opacity-100 -mt-[5px]"
            :icon="'camera.svg'"
            :width="28"
            :height="28"
            fill="none"
            @click="() => onPlay()"
          />
        </a-tooltip>
        <a-tooltip title="播放">
          <icon
            class="opacity-30 cursor-not-allowed"
            :icon="'play-circle.svg'"
            :width="20"
            :height="20"
            fill="none"
          />
        </a-tooltip>
        <a-tooltip title="重置">
          <icon
            :icon="'refresh.svg'"
            :width="20"
            :height="20"
            fill="none"
            @click="onReset"
          />
        </a-tooltip>
        <a-tooltip title="收起">
          <icon
            :icon="'fold.svg'"
            :width="20"
            :height="20"
            fill="none"
            @click="() => (expand = false)"
          />
        </a-tooltip>
      </div>
    </div>
    <!-- tabs -->
    <div class="w-full h-10 grid grid-cols-3 bg-black-card rounded-lg">
      <div
        v-for="cate in cates"
        :key="cate"
        class="transition-all h-full w-full flex items-center justify-center rounded-lg text-[12px]"
        :class="{ ' !bg-black-active': selectCate == cate }"
        @click="() => changeTab(cate)"
      >
        {{ cate }}
      </div>
    </div>
    <!-- inputs -->
    <div class="container h-full w-full overflow-y-auto box-border">
      <DataSourceConfigItem
        v-for="item in dataSourceItems"
        :key="item.key"
        :source-item="item"
        :keys="dataSourceKeys"
      />
      <template v-if="!dataSourceItems.length">
        <div class="mt-15 text-center opacity-70">
          暂未使用【{{ selectCate }}】数据项
        </div>
      </template>
    </div>

    <!-- 动效预览弹窗 -->
    <modal-watchface-record ref="recordModal" />
  </div>
</template>
<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import { dataSourceList } from '@/resources/data-source';
import {
  toRelatedSourceKeys,
  useDataSource,
  useEmitterListener
} from '@/hooks';
import { EventType } from '@/utils/emitter.utils';
import { debounce } from 'lodash-es';
import { mainRenderer } from '@/hooks/use-canvas-render';

const { reset, expand, configData } = useDataSource();

// 所有的数据类型
const cates = computed(() => Object.keys(dataSourceList));
// 选择的数据源分类
const selectCate = ref(cates.value[0]);

const onReset = () => {
  reset();
};

onMounted(() => {
  if (!Object.keys(configData).length) onReset();
});

const changeTab = (cate: string) => {
  selectCate.value = cate;
};

// 获取数据源
const dataSourceKeys = ref<string[]>([]);
const _setDataSourceKeys = () => {
  let keys: string[] = [];
  Object.keys(mainRenderer?.runtime.configData ?? {}).forEach((key) => {
    keys.push(...toRelatedSourceKeys(key));
  });

  // 添加12或24小时制
  if (
    keys.some(
      (key) =>
        key.startsWith('time') ||
        key.startsWith('weatherCurrentSun') ||
        key == 'appAlarm' ||
        key.startsWith('dualtime')
    )
  ) {
    keys.unshift('miscIs24H');
  }
  // 去重
  dataSourceKeys.value = [...new Set(keys)];
};

useEmitterListener(
  [EventType['maml:rendered'], EventType['renderer:rive-set-inputs']],
  debounce(_setDataSourceKeys, 30)
);

const dataSourceItems = computed(() => {
  const sourceItems = dataSourceList[selectCate.value];
  const res: { item: DataSourceItem; sort: number }[] = [];

  dataSourceKeys.value.forEach((key) => {
    const index = sourceItems.findIndex((item) => item.key == key);
    if (index > -1 && !sourceItems[index].hidden) {
      res.push({
        item: sourceItems[index],
        sort: key == 'miscIs24H' ? -999 : index
      });
    }
  });
  // 排序
  res.sort((a, b) => a.sort - b.sort);
  return res.map((item) => item.item);
});

// 监听元素绑定数据源
useEmitterListener(
  EventType['data-source:selected'],
  debounce((data: { sourceKey?: string } = {}) => {
    const { sourceKey } = data;
    if (!sourceKey) return;
    expand.value = true;
    Object.entries(dataSourceList).forEach((item) => {
      const [cate, list] = item;
      if (list.find((item) => item.key == sourceKey)) {
        selectCate.value = cate;
      }
    });
  }, 50)
);

useEmitterListener(EventType['watchface:opened'], () => {
  expand.value = false;
});

// 动态预览
const recordModal = ref();
const onPlay = () => recordModal.value?.open();
</script>

<style scoped></style>
